import { faClipboardList } from '@fortawesome/pro-light-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { ModalCenter, ModalCenterRef } from '@shared/components/ui/ModalCenter';
import React, { forwardRef, useImperativeHandle, useMemo, useRef, useState } from 'react';
import { toast } from '@shared/hooks/use-toast';
import {
  Button,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../../../shared/components/ui';
import { useCreatePreparation } from '../../hooks/useCreatePreparation';
import { useInfiniteScroll } from '../../hooks/useInfiniteScroll';
import { useOrderDetailsInfinite } from '../../hooks/useOrderDetailsInfinite';
import { OrderDetailsAccordion } from '../display/OrderDetailsAccordion';

// Tipos de preparación disponibles
export type PreparationType = 'masivo' | 'picking_packing';

interface SelectedProductData {
  productId: number;
  productName: string;
  productCode: string;
  productImage: string;
  quantity: number;
  orderIds: number[];
}

interface PreparationModalData {
  selectedProducts: SelectedProductData[];
  totalProducts: number;
}

// 🎯 Extender la interfaz del ref para incluir método personalizado
interface ExtendedModalCenterRef extends ModalCenterRef {
  openWithProducts: (data: PreparationModalData) => void;
}

interface PreparationModalProps {
  onPreparationSuccess?: () => void;
}

const PreparationModal = forwardRef<ExtendedModalCenterRef, PreparationModalProps>(({ onPreparationSuccess }, ref) => {
  // 🎯 Referencia interna al ModalCenter
  const internalModalRef = useRef<ModalCenterRef>(null);

  // 🎯 Estado interno del modal
  const [modalData, setModalData] = useState<PreparationModalData | null>(null);
  const [preparationType, setPreparationType] = useState<PreparationType>('picking_packing');
  const [isProcessing, setIsProcessing] = useState(false);

  // 🎯 Estado para acordeones expandidos
  const [expandedOrders, setExpandedOrders] = useState<Set<number>>(new Set());

  // 🎯 Estado para pedidos seleccionados (por defecto todos marcados)
  const [selectedOrderIds, setSelectedOrderIds] = useState<Set<number>>(new Set());

  // 🎯 Hook para crear preparación con callback de éxito
  const {
    mutateAsync: createPreparation,
    isPreparationSuccess,
    preparationError,
    reset: resetPreparationMutation,
  } = useCreatePreparation({
    onSuccess: (data) => {
      if (data.success) {
        // Llamar callback para limpiar selecciones en el componente padre
        onPreparationSuccess?.();

        // Cerrar modal después de un breve delay para mostrar el loader
        setTimeout(() => {
          internalModalRef.current?.closeModal();
        }, 1500); // 1.5 segundos para mostrar el loader
      }
    },
  });

  // 🎯 Memorizar cálculo de order IDs únicos para evitar recálculos constantes
  const uniqueOrderIds = useMemo(() => {
    if (!modalData?.selectedProducts) return [];
    const allOrderIds = modalData.selectedProducts.flatMap((product) => product.orderIds);
    return Array.from(new Set(allOrderIds));
  }, [modalData?.selectedProducts]);

  // 🎯 Hook para obtener detalles de pedidos con scroll infinito
  const {
    orders: orderDetails,
    isLoading: isLoadingDetails,
    error: detailsError,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
  } = useOrderDetailsInfinite(uniqueOrderIds, !!modalData && uniqueOrderIds.length > 0);

  // 🎯 Hook de scroll infinito
  const { lastElementRef } = useInfiniteScroll({
    hasNextPage,
    isFetchingNextPage,
    fetchNextPage,
    enabled: !!modalData && uniqueOrderIds.length > 0,
  });

  // 🎯 Inicializar pedidos seleccionados cuando se obtienen los uniqueOrderIds
  React.useEffect(() => {
    if (uniqueOrderIds.length > 0 && selectedOrderIds.size === 0) {
      // Marcar todos los pedidos por defecto (incluso antes de cargar detalles)
      setSelectedOrderIds(new Set(uniqueOrderIds));
    }
  }, [uniqueOrderIds, selectedOrderIds.size]);

  // 🎯 Memorizar funciones de toggle para evitar re-renders
  const toggleOrderExpansion = React.useCallback((orderId: number) => {
    setExpandedOrders((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(orderId)) {
        newSet.delete(orderId);
      } else {
        newSet.add(orderId);
      }
      return newSet;
    });
  }, []);

  const toggleOrderSelection = React.useCallback((orderId: number) => {
    setSelectedOrderIds((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(orderId)) {
        newSet.delete(orderId);
      } else {
        newSet.add(orderId);
      }
      return newSet;
    });
  }, []);

  // 🎯 Memorizar cálculo de productos afectados para evitar recálculos constantes
  const finalProductsData = useMemo(() => {
    if (!modalData) return { selectedProducts: [], totalProducts: 0 };

    const selectedProducts = modalData.selectedProducts.filter((product) =>
      product.orderIds.some((orderId) => selectedOrderIds.has(orderId)),
    );

    const totalProducts = selectedProducts.reduce((sum, product) => sum + product.quantity, 0);

    return { selectedProducts, totalProducts };
  }, [modalData, selectedOrderIds]);

  // 🎯 Método para abrir con datos específicos
  const openWithProducts = React.useCallback(
    (data: PreparationModalData) => {
      // Reiniciar estado de la mutación para evitar overlay persistente entre aperturas
      resetPreparationMutation();
      setModalData(data);
      setPreparationType('picking_packing'); // Reset al valor por defecto
      setIsProcessing(false);
      setExpandedOrders(new Set()); // Reset acordeones al abrir
      setSelectedOrderIds(new Set()); // Reset selección al abrir
      internalModalRef.current?.openModal();
    },
    [resetPreparationMutation],
  );

  // 🎯 Manejar confirmación de preparación con endpoint real
  const handleConfirmPreparation = React.useCallback(async () => {
    if (!modalData || isProcessing || selectedOrderIds.size === 0) return;

    setIsProcessing(true);

    try {
      const selectedOrderIdsArray = Array.from(selectedOrderIds);

      // Llamar al endpoint real
      const result = await createPreparation({
        orderIds: selectedOrderIdsArray,
        preparationType: preparationType,
      });

      // Considerar exitoso si hay preparationId o si success es true
      const isSuccess = result.success === true || (result.preparationId && result.preparationId > 0);

      if (isSuccess) {
        // Mostrar mensaje de éxito
        const preparationIdText = result.preparationId ? ` (ID: ${result.preparationId})` : '';
        toast({
          title: `✅ ${finalProductsData.totalProducts} productos de ${selectedOrderIds.size} pedidos enviados a preparación ${preparationType === 'masivo' ? 'masiva' : 'picking y packing'} correctamente${preparationIdText}.`,
          variant: 'default',
        });
      } else {
        // Mostrar mensaje de error del servidor
        toast({
          title: `❌ Error: ${result.message || 'No se pudo crear la preparación'}`,
          variant: 'destructive',
        });
      }
    } catch (error) {
      console.error('Error al crear preparación:', error);

      // Mostrar mensaje de error más específico
      const errorMessage = error instanceof Error ? error.message : 'Error inesperado al crear la preparación';
      alert(`❌ ${errorMessage}`);
    } finally {
      setIsProcessing(false);
    }
  }, [modalData, isProcessing, selectedOrderIds, preparationType, finalProductsData, createPreparation]);

  // 🎯 Manejar cancelación
  const handleCancel = React.useCallback(() => {
    if (!isProcessing) {
      // Reiniciar estado de éxito/error al cerrar manualmente
      resetPreparationMutation();
      internalModalRef.current?.closeModal();
    }
  }, [isProcessing, resetPreparationMutation]);

  const handleModalClose = React.useCallback(() => {
    // Reiniciar estado de la mutación también al cerrar con la X o fuera del modal
    resetPreparationMutation();
  }, [resetPreparationMutation]);

  // 🎯 Exponer métodos usando useImperativeHandle
  useImperativeHandle(
    ref,
    () => ({
      openModal: () => internalModalRef.current?.openModal(),
      closeModal: () => internalModalRef.current?.closeModal(),
      openWithProducts,
    }),
    [openWithProducts],
  );

  // 🎯 Estado combinado de procesamiento (creando + actualizando lista)
  const isProcessingComplete = isProcessing || (isPreparationSuccess && !preparationError);

  // Si no hay datos, no renderizar contenido
  if (!modalData) {
    return (
      <ModalCenter
        ref={internalModalRef}
        title="Confirmar preparación"
        width="xl"
        type="info"
        onCloseModal={handleModalClose}
      >
        <div>Cargando...</div>
      </ModalCenter>
    );
  }

  return (
    <ModalCenter
      ref={internalModalRef}
      title="Confirmar preparación"
      width="xl"
      type="info"
      onCloseModal={handleModalClose}
    >
      <div className="space-y-4 relative" data-testid="modal-confirmar-preparacion-content">
        {/* Overlay de loading cuando está actualizando la lista */}
        {isPreparationSuccess && !preparationError && (
          <div className="absolute inset-0 bg-background/80 backdrop-blur-sm z-50 flex items-center justify-center rounded-lg">
            <div className="flex flex-col items-center gap-3 p-6">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
              <div className="text-center">
                <p className="font-medium text-foreground">¡Preparación creada!</p>
                <p className="text-sm text-muted-foreground">Actualizando lista de pedidos...</p>
              </div>
            </div>
          </div>
        )}

        {/* Detalles de Pedidos */}
        <div>
          <div className="flex items-center justify-between mb-3">
            <div className="flex items-center gap-3">
              <h4 className="font-medium text-foreground text-md">Pedidos a preparar ({selectedOrderIds.size})</h4>
            </div>
          </div>

          <OrderDetailsAccordion
            orders={orderDetails}
            isLoading={isLoadingDetails}
            error={!!detailsError}
            isFetchingNextPage={isFetchingNextPage}
            lastElementRef={lastElementRef}
            expandedOrders={expandedOrders}
            onToggleExpansion={toggleOrderExpansion}
            enableSelection={true}
            selectedOrderIds={selectedOrderIds}
            onToggleSelection={toggleOrderSelection}
            maxHeight="max-h-96"
            showBorder={true}
            loadingMessage="Cargando detalles..."
          />
        </div>

        {/* Selector de tipo de preparación */}
        <div className="space-y-3">
          <div className="flex items-center gap-2">
            <label className="text-md font-medium text-foreground">Tipo de preparación</label>
          </div>
          <Select
            value={preparationType}
            onValueChange={(value) => setPreparationType(value as PreparationType)}
            disabled={isProcessing}
          >
            <SelectTrigger className="h-12 bg-background border-2 hover:border-primary/50 transition-colors">
              <SelectValue placeholder="Selecciona el tipo de preparación" />
            </SelectTrigger>
            <SelectContent align="start" className="w-full">
              {/* OPCIÓN OCULTA TEMPORALMENTE - Preparación Masiva */}
              {/* <SelectItem value="masivo" className="text-left p-4 cursor-pointer">
                                <div className="flex items-center gap-3">
                                    <div className="bg-blue-100 dark:bg-blue-900/50 p-2 rounded-lg">
                                        <FontAwesomeIcon icon={faBoxOpen} className="w-4 h-4 text-blue-600 dark:text-blue-400" />
                                    </div>
                                    <div className="flex flex-col items-start text-left">
                                        <span className="font-semibold">Preparación Masiva</span>
                                        <span className="text-xs text-muted-foreground">Todos los productos se preparan en lote</span>
                                    </div>
                                </div>
                            </SelectItem> */}
              <SelectItem value="picking_packing" className="text-left p-4 cursor-pointer">
                <div className="flex items-center gap-3">
                  <div className="bg-green-100 dark:bg-green-900/50 p-2 rounded-lg">
                    <FontAwesomeIcon icon={faClipboardList} className="w-4 h-4 text-green-600 dark:text-green-400" />
                  </div>
                  <div className="flex flex-col items-start text-left">
                    <span className="font-semibold">Picking y Packing</span>
                    <span className="text-xs text-muted-foreground">Preparación individual por pedido</span>
                  </div>
                </div>
              </SelectItem>
            </SelectContent>
          </Select>
        </div>

        {/* Botones de acción */}
        <div className="flex items-center justify-end gap-3 pt-4 border-t border-border">
          <Button variant="outline" onClick={handleCancel} disabled={isProcessingComplete}>
            Cancelar
          </Button>
          <Button
            onClick={handleConfirmPreparation}
            disabled={isProcessingComplete || selectedOrderIds.size === 0}
            className="min-w-40"
          >
            {isProcessingComplete ? (
              <>
                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                {isPreparationSuccess ? 'Actualizando lista...' : 'Procesando...'}
              </>
            ) : (
              `Confirmar preparación (${selectedOrderIds.size})`
            )}
          </Button>
        </div>
      </div>
    </ModalCenter>
  );
});

PreparationModal.displayName = 'PreparationModal';

export { PreparationModal };
export type { PreparationModalProps, ExtendedModalCenterRef as PreparationModalRef, SelectedProductData };
